import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  api,
  getAccessToken,
  MeResponse,
  setAccessToken,
  TokenPair,
} from './api';
import { applyBranding, clearBranding } from './branding';

interface AuthState {
  user: MeResponse | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<TokenPair>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  setupPassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });

  const refreshMe = useCallback(async () => {
    if (!getAccessToken()) {
      // Versuche Cookie-Refresh, falls Refresh-Cookie da
      try {
        const tokens = await api<TokenPair>('/api/auth/refresh', {
          method: 'POST',
          skipAuth: true,
        });
        setAccessToken(tokens.access_token);
      } catch {
        setState({ user: null, loading: false });
        return;
      }
    }
    try {
      const me = await api<MeResponse>('/api/auth/me');
      applyBranding(me.branding);
      setState({ user: me, loading: false });
    } catch {
      clearBranding();
      setState({ user: null, loading: false });
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const login = useCallback(
    async (email: string, password: string): Promise<TokenPair> => {
      const tokens = await api<TokenPair>('/api/auth/login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      });
      setAccessToken(tokens.access_token);
      const me = await api<MeResponse>('/api/auth/me');
      applyBranding(me.branding);
      setState({ user: me, loading: false });
      return tokens;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST', skipAuth: true });
    } catch {
      // ignore — client-side logout immer durchführen
    }
    setAccessToken(null);
    clearBranding();
    setState({ user: null, loading: false });
  }, []);

  const setupPassword = useCallback(
    async (newPassword: string) => {
      await api('/api/auth/setup-password', {
        method: 'POST',
        body: { new_password: newPassword },
      });
      await refreshMe();
    },
    [refreshMe],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refreshMe, setupPassword }),
    [state, login, logout, refreshMe, setupPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be inside AuthProvider');
  }
  return ctx;
}
